import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { contactsApi, companiesApi, usersApi } from '../api';
import { ArrowLeft, Pencil, Save, X, Trash2, Plus, Building2, CheckSquare } from 'lucide-react';
import SearchableSelect from '../components/SearchableSelect';
import TaskModal from '../components/TaskModal';
import dayjs from 'dayjs';

const statusLabel: Record<string, string> = { PENDING: 'Ожидает', IN_PROGRESS: 'В работе', COMPLETED: 'Выполнена', CANCELLED: 'Отменена' };
const statusColor: Record<string, string> = { PENDING: 'badge-warning', IN_PROGRESS: 'badge-info', COMPLETED: 'badge-success', CANCELLED: 'badge-default' };

export default function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contact, setContact] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [showTaskModal, setShowTaskModal] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await contactsApi.getById(id);
      setContact(res.data);
      setForm({
        firstName: res.data.firstName || '',
        lastName: res.data.lastName || '',
        middleName: res.data.middleName || '',
        position: res.data.position || '',
        companyId: res.data.companyId || '',
        phones: res.data.phones?.join(', ') || '',
        emails: res.data.emails?.join(', ') || '',
        messengers: res.data.messengers?.join(', ') || '',
        birthday: res.data.birthday ? res.data.birthday.split('T')[0] : '',
        tags: res.data.tags?.join(', ') || '',
        status: res.data.status || '',
        responsibleId: res.data.responsibleId || '',
      });
    } catch (e) {
      console.error('Load contact error:', e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [compRes, usersRes] = await Promise.all([
          companiesApi.list({ limit: 100 }),
          usersApi.list({ limit: 100 }),
        ]);
        setCompanies(compRes.data.data || []);
        setUsers(usersRes.data.data || []);
      } catch {}
    };
    loadDropdowns();
  }, []);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const data = {
        ...form,
        phones: form.phones.split(',').map((s: string) => s.trim()).filter(Boolean),
        emails: form.emails.split(',').map((s: string) => s.trim()).filter(Boolean),
        messengers: form.messengers.split(',').map((s: string) => s.trim()).filter(Boolean),
        tags: form.tags.split(',').map((s: string) => s.trim()).filter(Boolean),
        companyId: form.companyId || null,
        responsibleId: form.responsibleId || null,
        birthday: form.birthday || null,
      };
      await contactsApi.update(id, data);
      setEditing(false);
      load();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm('Удалить контакт?')) return;
    try {
      await contactsApi.remove(id);
      navigate('/contacts');
    } catch (e) {
      alert('Ошибка удаления');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Загрузка...</div>;
  if (!contact) return <div style={{ padding: 40, textAlign: 'center' }}>Контакт не найден</div>;

  return (
    <div>
      <div className="detail-header">
        <button className="detail-back-btn" onClick={() => navigate('/contacts')}>
          <ArrowLeft size={20} />
        </button>
        <div className="detail-header-info">
          <div className="detail-header-title">{contact.firstName} {contact.lastName}</div>
          <div className="detail-header-subtitle">
            {contact.position && <span>{contact.position}</span>}
            {contact.company && <span style={{ marginLeft: contact.position ? 8 : 0, color: 'var(--color-text-muted)' }}>· {contact.company.name}</span>}
          </div>
        </div>
        <div className="detail-header-actions">
          {editing ? (
            <>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                <Save size={16} /> {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(false); load(); }}>
                <X size={16} /> Отмена
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
                <Pencil size={16} /> Редактировать
              </button>
              <button className="btn btn-danger btn-sm" onClick={handleDelete}>
                <Trash2 size={16} /> Удалить
              </button>
            </>
          )}
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-grid-main">
          <div className="card">
            <div className="card-header">
              <div className="card-title">Информация о контакте</div>
            </div>
            <div className="card-body">
              {editing ? (
                <>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <label className="form-label">Имя *</label>
                      <input className="form-input" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Фамилия *</label>
                      <input className="form-input" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} required />
                    </div>
                  </div>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <label className="form-label">Отчество</label>
                      <input className="form-input" value={form.middleName} onChange={e => setForm({ ...form, middleName: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Должность</label>
                      <input className="form-input" value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <SearchableSelect
                        label="Компания"
                        value={form.companyId}
                        onChange={val => setForm({ ...form, companyId: val })}
                        options={companies.map((c: any) => ({ value: c.id, label: c.name }))}
                        emptyLabel="— Без компании —"
                      />
                    </div>
                  </div>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <label className="form-label">Телефоны (через запятую)</label>
                      <input className="form-input" value={form.phones} onChange={e => setForm({ ...form, phones: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email (через запятую)</label>
                      <input className="form-input" value={form.emails} onChange={e => setForm({ ...form, emails: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <label className="form-label">Мессенджеры</label>
                      <input className="form-input" value={form.messengers} onChange={e => setForm({ ...form, messengers: e.target.value })} placeholder="telegram: @username" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Дата рождения</label>
                      <input className="form-input" type="date" value={form.birthday} onChange={e => setForm({ ...form, birthday: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <label className="form-label">Теги (через запятую)</label>
                      <input className="form-input" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <SearchableSelect
                        label="Ответственный"
                        value={form.responsibleId}
                        onChange={val => setForm({ ...form, responsibleId: val })}
                        options={users.map((u: any) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))}
                        emptyLabel="— Не назначен —"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="detail-field-grid">
                  <div className="detail-field">
                    <div className="detail-field-label">Имя</div>
                    <div className="detail-field-value">{contact.firstName}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Фамилия</div>
                    <div className="detail-field-value">{contact.lastName}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Отчество</div>
                    <div className="detail-field-value">{contact.middleName || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Должность</div>
                    <div className="detail-field-value">{contact.position || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Компания</div>
                    <div className="detail-field-value">
                      {contact.company ? <Link to={`/companies/${contact.company.id}`} className="detail-link">{contact.company.name}</Link> : <span className="empty">—</span>}
                    </div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Дата рождения</div>
                    <div className="detail-field-value">{contact.birthday ? dayjs(contact.birthday).format('DD.MM.YYYY') : <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Телефоны</div>
                    <div className="detail-field-value">{contact.phones?.length ? contact.phones.join(', ') : <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Email</div>
                    <div className="detail-field-value">{contact.emails?.length ? contact.emails.join(', ') : <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Мессенджеры</div>
                    <div className="detail-field-value">{contact.messengers?.length ? contact.messengers.join(', ') : <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Статус</div>
                    <div className="detail-field-value">{contact.status || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field detail-field-full">
                    <div className="detail-field-label">Теги</div>
                    <div className="detail-field-value">
                      {contact.tags?.length ? (
                        <div className="detail-tags">{contact.tags.map((t: string, i: number) => <span key={i} className="detail-tag">{t}</span>)}</div>
                      ) : <span className="empty">—</span>}
                    </div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Ответственный</div>
                    <div className="detail-field-value">{contact.responsible ? `${contact.responsible.firstName} ${contact.responsible.lastName}` : <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Создано</div>
                    <div className="detail-field-value">{dayjs(contact.createdAt).format('DD.MM.YYYY HH:mm')}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="detail-grid-sidebar">
          {contact.company && (
            <div className="card">
              <div className="card-header">
                <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Building2 size={16} /> Компания
                </div>
              </div>
              <div className="card-body" style={{ padding: 12 }}>
                <Link to={`/companies/${contact.company.id}`} className="detail-related-item">
                  <div className="detail-related-item-info">
                    <div className="detail-related-item-title">{contact.company.name}</div>
                    <div className="detail-related-item-meta">{contact.company.industry || contact.company.type || '—'}</div>
                  </div>
                </Link>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckSquare size={16} /> Задачи ({contact.tasks?.length || 0})
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowTaskModal(true)}>
                <Plus size={14} /> Добавить
              </button>
            </div>
            <div className="card-body" style={{ padding: contact.tasks?.length ? 12 : 24 }}>
              {contact.tasks?.length ? contact.tasks.map((t: any) => (
                <Link key={t.id} to={`/tasks/${t.id}`} className="detail-related-item">
                  <div className="detail-related-item-info">
                    <div className="detail-related-item-title" style={{ textDecoration: t.isCompleted ? 'line-through' : 'none' }}>{t.title}</div>
                    <div className="detail-related-item-meta">
                      {t.dueDate ? dayjs(t.dueDate).format('DD.MM.YYYY') : '—'} · {t.assignee?.firstName} {t.assignee?.lastName}
                    </div>
                  </div>
                  <div className="detail-related-item-badge">
                    <span className={`badge ${statusColor[t.status] || 'badge-default'}`}>{statusLabel[t.status] || t.status}</span>
                  </div>
                </Link>
              )) : <div className="detail-related-empty">Нет задач</div>}
            </div>
          </div>
        </div>
      </div>

      {showTaskModal && (
        <TaskModal
          contactId={id}
          companyId={contact.companyId || undefined}
          onClose={() => setShowTaskModal(false)}
          onSave={() => { setShowTaskModal(false); load(); }}
        />
      )}
    </div>
  );
}
